import { useTemplateUpdateCheck } from '../hooks/useTemplateUpdateCheck';
import { useFeatureFlags } from '../hooks/useFeatureFlags';

/**
 * Compact pill for the dashboard GameCard. Shows one of:
 * - nothing (up to date, or check failed — the user isn't blocked)
 * - "Update: v0.5 → v0.6" (hasUpdate, non-breaking)
 * - "Update (breaking): v0.5 → v0.6" (hasUpdate, breaking — louder colour)
 *
 * Click handler is wired by the parent (GameCard) so the card can open
 * its own update modal / trigger its own flow.
 */
export function TemplateUpdateBadge({
    gameId,
    onClick,
    enabled,
}: {
    gameId: string;
    onClick: (e: React.MouseEvent) => void;
    /**
     * Dashboard lazy-load path (`docs/dashboard-lazy-load-plan.md`
     * lines 109-140): set to `false` while the card is below the fold
     * so the IPC handler — which shells out to `git ls-remote` — only
     * fires for visible cards. Defaults to `true` to preserve the
     * pre-lazy behaviour for non-Dashboard call sites.
     */
    enabled?: boolean;
}): React.JSX.Element | null {
    const { data } = useTemplateUpdateCheck(gameId, { enabled });
    const { newUi } = useFeatureFlags();
    if (!data || !data.hasUpdate) return null;

    const tone = data.isBreaking
        ? newUi
          ? 'bg-[#FB7928]/20 text-[#FB7928] hover:bg-[#FB7928]/30'
          : 'bg-amber-500/20 text-amber-300 hover:bg-amber-500/30'
        : newUi
          ? 'bg-[#BCE107]/20 text-[#BCE107] hover:bg-[#BCE107]/30'
          : 'bg-sky-500/20 text-sky-300 hover:bg-sky-500/30';

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={(e) => {
                e.stopPropagation();
                onClick(e);
            }}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                    e.preventDefault();
                    onClick(e as unknown as React.MouseEvent);
                }
            }}
            title={
                data.isBreaking
                    ? `Update available (breaking): v${data.current} → v${data.latest}. Click to review.`
                    : `Update available: v${data.current} → v${data.latest}. Click to apply.`
            }
            className={`shrink-0 cursor-pointer rounded-full px-2 py-0.5 text-[10px] font-medium transition-colors ${tone}`}
        >
            {data.isBreaking ? 'Update!' : 'Update'} v{data.latest}
        </div>
    );
}
